// track texting numbers

import { createSlice, Dispatch, PayloadAction } from '@reduxjs/toolkit'
import { RegisteredTwilioNumber, SessionV2, ConversationType } from '../../../../interfaces/db'
import { getServicesManager } from 'services'
import uuid from 'react-uuid'
import { MessagingDataColumns, SessionV2Columns } from '../../../../interfaces/table'
import { SessionListResponse, TypedResultMap } from '../../../../interfaces/sessions'

// Redux-safe version of RegisteredTwilioNumber with serializable dates
export type ReduxSafeRegisteredTwilioNumber = Omit<RegisteredTwilioNumber, 'nomorobo_spamscore_as_of' | 'free_caller_registry_status_as_of'> & {
  nomorobo_spamscore_as_of: string | null;
  free_caller_registry_status_as_of: string | null;
};

// Helper functions to convert between Redux-safe and original types
export function toReduxSafeRegisteredTwilioNumber(number: RegisteredTwilioNumber): ReduxSafeRegisteredTwilioNumber {
  return {
    ...number,
    nomorobo_spamscore_as_of: number.nomorobo_spamscore_as_of ? number.nomorobo_spamscore_as_of.toISOString() : null,
    free_caller_registry_status_as_of: number.free_caller_registry_status_as_of ? number.free_caller_registry_status_as_of.toISOString() : null
  };
}

export function fromReduxSafeRegisteredTwilioNumber(number: ReduxSafeRegisteredTwilioNumber): RegisteredTwilioNumber {
  return {
    ...number,
    nomorobo_spamscore_as_of: number.nomorobo_spamscore_as_of ? new Date(number.nomorobo_spamscore_as_of) : null,
    free_caller_registry_status_as_of: number.free_caller_registry_status_as_of ? new Date(number.free_caller_registry_status_as_of) : null
  };
}

interface TextingNumbersState {
  textingNumbersLoaded: boolean
  textingNumbers: ReduxSafeRegisteredTwilioNumber[] | null
}

const textingNumbersInitialState: TextingNumbersState = {
  textingNumbersLoaded: false,
  textingNumbers: null,
}       

export const textingNumbersSlice = createSlice({
  name: 'textingNumbers',
  initialState: textingNumbersInitialState,
  reducers: {
    setTextingNumbers: (state, action: PayloadAction<ReduxSafeRegisteredTwilioNumber[] | null>) => {
      state.textingNumbers = action.payload
    },
    setTextingNumbersLoaded: (state, action: PayloadAction<boolean>) => {
      state.textingNumbersLoaded = action.payload
    },
  },
})

export const { setTextingNumbers, setTextingNumbersLoaded } = textingNumbersSlice.actions

export function reloadTextingNumbers(dispatch: Dispatch) {
    getServicesManager().getTextingNumbers().then((textingNumbers) => {
        if (textingNumbers) {
            // Convert to Redux-safe format
            const reduxSafeNumbers = textingNumbers.map(toReduxSafeRegisteredTwilioNumber);
            dispatch(setTextingNumbers(reduxSafeNumbers));
        } else {
            dispatch(setTextingNumbers(null));
        }
        dispatch(setTextingNumbersLoaded(true));
    });
}

export interface TextingStatus {
    status: 'pending' | 'sent' | 'failed'
    error: string | null
}

export interface PendingSend {
    uuid: string
    session_id: string | null
    target_phone_number: string
    sent_number: ReduxSafeRegisteredTwilioNumber
    message: string
    status: TextingStatus
    start: string // Store as ISO string for Redux
}

export interface SentTextMessage {
    uuid: string
    target_phone_number: string
    session: SessionListResponse
}

export interface SentTextMessages {
    pendingSend: PendingSend[]
    sent: SentTextMessage[]
}

const textingMessagesInitialState: SentTextMessages = {
    pendingSend: [],
    sent: [],
}


export const textingMessagesSlice = createSlice({
    name: 'textingMessages',
    initialState: textingMessagesInitialState as SentTextMessages,
    reducers: {
        updatePendingSend: (state, action: PayloadAction<PendingSend>) => {
            const index = state.pendingSend.findIndex((pendingSend) => pendingSend.uuid === action.payload.uuid)
            if (index !== -1) {
                state.pendingSend[index] = action.payload
            }
        },
        removePendingSend: (state, action: PayloadAction<string>) => {
            state.pendingSend = state.pendingSend.filter((pendingSend) => pendingSend.uuid !== action.payload)
        },
        addPendingSend: (state, action: PayloadAction<PendingSend>) => {
            state.pendingSend.push(action.payload)
        },
        addSent: (state, action: PayloadAction<SentTextMessage>) => {
            state.sent.push(action.payload)
        },
    },
})

export const { removePendingSend, addPendingSend, addSent, updatePendingSend } = textingMessagesSlice.actions

export interface SendTextMessageProps {
    dispatch: Dispatch
    from: ReduxSafeRegisteredTwilioNumber
    to: string
    message: string
}

export async function retryTextMessage(params: PendingSend, dispatch: Dispatch) {
    const { uuid, target_phone_number, sent_number, message, session_id } = params
    dispatch(updatePendingSend({
        ...params,
        status: {
            status: 'pending',
            error: null,
        }
    }))
    // Convert back to original type for API call
    const originalNumber = fromReduxSafeRegisteredTwilioNumber(sent_number);
    const result = await getServicesManager().sendTextMessage(originalNumber, target_phone_number, message)
    if (!result) {
        console.error('Failed to send text message', params)
        dispatch(updatePendingSend({
            ...params,
            status: {
                status: 'failed',
                error: 'Failed to send text message'
            }
        }))
    } else {
        dispatch(addSent({
            uuid: params.uuid,
            session: {
                session_id: result.session_id,
                start: result.started_at,
                customer_address: result.customer_address,
                agent_address: result.agent_address,
                user_id: result.resource_id,
                conversation_type: result.conversation_type,
                result: new TypedResultMap([
                    [MessagingDataColumns.AGENT_BODIES_STR, JSON.stringify([params.message])],
                    [MessagingDataColumns.AGENT_TIMES_STR, "0"],
                    [MessagingDataColumns.CUSTOMER_BODIES_STR, ""],
                    [MessagingDataColumns.CUSTOMER_TIMES_STR, ""],
                ]),
            },
            target_phone_number: params.target_phone_number,
        }))
        dispatch(removePendingSend(params.uuid))
    }
}

export async function sendTextMessage(params: SendTextMessageProps) {
    const { dispatch, from, to, message } = params  
    const uuid_value = uuid()
    const pendingSend: PendingSend = {
        uuid: uuid_value,
        target_phone_number: to,
        sent_number: from,
        message: message,
        session_id: null,
        start: new Date().toISOString(), // Store as ISO string
        status: {
            status: 'pending',
            error: null,
        },
    }
    dispatch(addPendingSend(pendingSend))
    // Convert back to original type for API call
    const originalNumber = fromReduxSafeRegisteredTwilioNumber(from);
    const result = await getServicesManager().sendTextMessage(originalNumber, to, message)
    if (!result) {
        console.error('Failed to send text message', pendingSend)
        dispatch(updatePendingSend({...pendingSend, status: {
            status: 'failed',
            error: 'Failed to send text message',
        }}))
    } else {
        dispatch(addSent({
            uuid: uuid_value,
            session: {
                session_id: result.session_id,
                start: result.started_at,
                customer_address: result.customer_address,
                agent_address: result.agent_address,
                user_id: result.resource_id,
                conversation_type: result.conversation_type,
                result: new TypedResultMap([
                    [MessagingDataColumns.AGENT_BODIES_STR, JSON.stringify([params.message])],
                    [MessagingDataColumns.AGENT_TIMES_STR, "0"],
                    [MessagingDataColumns.CUSTOMER_BODIES_STR, ""],
                    [MessagingDataColumns.CUSTOMER_TIMES_STR, ""],
                ]),
            },
            target_phone_number: to,
        }))
        dispatch(removePendingSend(uuid_value))
    }
}


export interface TextMessage {
    sessionId: string
    body: string
    fromAddress: string
    sentAt: number
    messageId: number
    delivered: boolean | null
    deliverySetAt: number | null
}

export interface TextSessionState {
    sessionIdToTextMessages: { [key: string]: TextMessage[] }
    allMessages: SessionListResponse[]
    numberToMessages: {
        [key: string]: SessionListResponse[]
    }
}

const textSessionInitialState: TextSessionState = {
    sessionIdToTextMessages: {},
    allMessages: [],
    numberToMessages: {},
}

export const textSessionSlice = createSlice({
    name: 'textSession',
    initialState: textSessionInitialState,
    reducers: {
        addMessage: (state, action: PayloadAction<{session: SessionV2, message: TextMessage}>) => {
            const { session, message } = action.payload
            // need to construct the session list response that we will associate to this phone number
            // existing text messages for this session
            const existingTextMessages = state.sessionIdToTextMessages[session.session_id] || []
            // check if we need to override an existing session
            const matchingTextMessage = existingTextMessages.findIndex(m => m.messageId === message.messageId)
            if (matchingTextMessage !== -1) existingTextMessages[matchingTextMessage] = message
            else existingTextMessages.push(message)
            const sortedTextMessages = existingTextMessages.sort((a, b) => a.messageId - b.messageId)
            state.sessionIdToTextMessages[session.session_id] = sortedTextMessages

            // construct the session list response
            const sessionListResponse = convertTextMessagesToSessionListResponse(session, sortedTextMessages)
            const allMessagesIndex = state.allMessages.findIndex(s => s.session_id === session.session_id)
            if (allMessagesIndex !== -1) state.allMessages[allMessagesIndex] = sessionListResponse
            else state.allMessages.push(sessionListResponse)
            if (session.customer_address) {
                const messages = state.numberToMessages[session.customer_address] || []
                const idx = messages.findIndex(m => m.session_id === session.session_id)
                if (idx !== -1) messages[idx] = sessionListResponse
                else messages.push(sessionListResponse)
                state.numberToMessages[session.customer_address] = messages
            }
        },
    }
})

export function convertTextMessagesToSessionListResponse(session: SessionV2, textMessages: TextMessage[]): SessionListResponse {
    const result = new TypedResultMap()
    const allMessages = textMessages.sort((a, b) => a.messageId - b.messageId)
    const allMessagesWithTimes = allMessages.map((m, i) => {
        if (i === 0) return {...m, sentAt: m.sentAt ?? 0}
        let maxTime = allMessages.slice(0, i).reduce((acc, m) => Math.max(acc, m.sentAt ?? 0), 0)
        let time = Math.max(m.sentAt ?? 0, maxTime + 1*i)
        return {...m, sentAt: time}
    })
    const agentMessagesWithTimes = allMessagesWithTimes.filter(m => session.agent_address && (m.fromAddress.includes(session.agent_address) || session.agent_address.includes(m.fromAddress)))
    const customerMessagesWithTimes = allMessagesWithTimes.filter(m => session.customer_address && (m.fromAddress.includes(session.customer_address) || session.customer_address.includes(m.fromAddress)))
    
    result.set(MessagingDataColumns.AGENT_BODIES_STR, JSON.stringify(agentMessagesWithTimes.map(m => m.body)))
    result.set(MessagingDataColumns.AGENT_TIMES_STR, agentMessagesWithTimes.map(m => m.sentAt).join(','))
    result.set(MessagingDataColumns.CUSTOMER_BODIES_STR, JSON.stringify(customerMessagesWithTimes.map(m => m.body)))
    result.set(MessagingDataColumns.CUSTOMER_TIMES_STR, customerMessagesWithTimes.map(m => m.sentAt).join(','))
    result.set(SessionV2Columns.SESSION_ID, session.session_id)
    result.set(SessionV2Columns.STARTED_AT, session.started_at)
    if (session.agent_address) result.set(SessionV2Columns.AGENT_ADDRESS, session.agent_address)
    if (session.customer_address) result.set(SessionV2Columns.CUSTOMER_ADDRESS, session.customer_address)
    if (session.resource_id) result.set(SessionV2Columns.RESOURCE_ID, session.resource_id)
    result.set(SessionV2Columns.CONVERSATION_TYPE, session.conversation_type)

    return {
        session_id: session.session_id,
        start: session.started_at,
        customer_address: session.customer_address,
        agent_address: session.agent_address,
        user_id: session.resource_id,
        conversation_type: session.conversation_type,
        result: result
    }
}


export const { 
    addMessage, 
} = textSessionSlice.actions